/**
 * Copyright ToolBelt Data Inc., 2021 All Rights Reserved
 */

import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Row,
    Col,
    Image,
    Button,
} from 'react-bootstrap';
import {
    map,
    findIndex,
    uniq,
    uniqBy,
} from 'lodash';

import config from '../../../../config';

import { parseCubeFilter } from '../../CubeFilterBuilder';
import { useCubeNamespace } from "../../../../common/utils/universeUtils";

import { hiddenFilters } from "../../hiddenFilters";
import { replaceFilters } from "../../universeSlice";
import { countUniverse, searchUniverse } from "../../universeQuerySlice";

import ExploreMapFlyoutDetailLoadingSpinner from './ExploreMapFlyoutDetailLoadingSpinner';
import ExploreMapFlyoutDetailSelectedProduct from './ExploreMapFlyoutDetailSelectedProduct';
import ExploreMapFlyoutDetailLocation from "./ExploreMapFlyoutDetailLocation";

import * as CubeApi from '../../../../common/CubeApi';
import mapMarkerIcon from "../../../../common/assets/img/ToolbeltIcons-15.png";

export default function ExploreMapFlyoutDetailSelectedProductClustered(props) {
    const dispatch = useDispatch();

    const currentUniverse = useSelector(state => state.universe.currentUniverse);
    const currentUniverseData = useSelector(state => state.universe.universes[currentUniverse]);
    const toggleMap = currentUniverseData?.toggleMap ?? true;

    const filterEntities = currentUniverseData?.entities || {};
    const currentFilters = Object.values(filterEntities);

    const productDataType = useSelector(
        state => state.universeQuery.results[currentUniverse]?.productDataType || 'RECORD'
    );

    const cubeNamespace = useCubeNamespace();

    const [loading, setLoading] = useState(true);
    const [selectedRecord, setSelectedRecord] = useState(null);

    useEffect(() => {
        console.log('ExploreMapFlyoutDetailSelectedProductCluster - Product Cluster');
        setLoading(true);
        _getRecord(props, productDataType, cubeNamespace)
            .then((response) => {
                setLoading(false);
                setSelectedRecord(response);
            });
    }, [props.selectedPoint, productDataType, cubeNamespace]);

    const handleAddToFilter = () => {
        const { selectedLocations } = props.selectedPoint;

        const filterId = productDataType === 'RECORD'
            ? hiddenFilters.COMPANY.COMPANY_ID.id
            : hiddenFilters.COMPANY.COMPANY_ADDRESS_CITY_REGION_FORMATTED.id;

        const locationFilter = productDataType === 'RECORD'
            ? ({
                ...hiddenFilters.COMPANY.COMPANY_IDENTITY,
                value: map(selectedLocations, (loc) => ({ id: loc.id }))
            })
            : ({
                ...hiddenFilters.COMPANY.COMPANY_ADDRESS_CITY_REGION_FORMATTED,
                value: map(
                    uniqBy(selectedLocations, 'company_address_city_region_formatted'),
                    (loc) => ({ id: loc.company_address_city_region_formatted })
                )
            });

        const index = findIndex(currentFilters, (ft) => ft.id === filterId);
        const updatedFilters = [...currentFilters];
        if (index >= 0) {
            updatedFilters.splice(index, 1, locationFilter);
        } else {
            updatedFilters.push(locationFilter);
        }

        dispatch(replaceFilters(updatedFilters));
        dispatch(countUniverse({ universeKey: currentUniverse, isMapEnabled: toggleMap }));
        dispatch(searchUniverse({ universeKey: currentUniverse }));
    };

    const isDisabledAddToFilter = () => {
        return false;
    };

    if (loading) return (<ExploreMapFlyoutDetailLoadingSpinner />);

    if (selectedRecord) {
        const {
            selectedLocations,
            countCompanies,
            countContacts,
            tradeTypes,
            bestLocation,
        } = selectedRecord;

        const _companyLocations = map(selectedLocations, (location) => location.company_address_city_region_formatted);
        const companyLocations = uniq(_companyLocations);

        return (
            <Row>
                <Col>
                    <div className="explore-map-flyout-detail">
                        {tradeTypes.length > 0 ? (
                            <div>
                                <div>
                                    <ExploreMapFlyoutDetailLocation
                                        bestLocation={bestLocation}
                                        companyLocations={companyLocations}
                                        countCompanies={countCompanies}
                                        countContacts={countContacts}
                                        isClustered
                                    />
                                </div>
                                <div>
                                    <ExploreMapFlyoutDetailSelectedProduct
                                        tradeTypes={tradeTypes}
                                        countCompanies={countCompanies}
                                        countContacts={countContacts}
                                        countPlaces={companyLocations.length}
                                        bestLocationName={bestLocation.company_address_city_region_formatted}
                                    />
                                </div>
                                <div className="action-container">
                                    <Button
                                        style={{ margin: '2px' }}
                                        className="action-button btn-height-35 btn-min-width-120"
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={isDisabledAddToFilter()}
                                        onClick={() => handleAddToFilter()}
                                    >
                                        ADD To FILTER
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className={'selected-map-point'}>
                                <Image
                                    className="icon"
                                    src={mapMarkerIcon}
                                    alt="ToolBeltData Company Icon"
                                    fluid
                                />
                                <div>Select a point on the map to get started!</div>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        );
    }

    return null;
}

ExploreMapFlyoutDetailSelectedProductClustered.propTypes = {
    selectedPoint: PropTypes.object,
};

async function _getRecord(props, productDataType, cubeNamespace) {
    const {
        currentFilters: propsFilters,
    } = props;

    const {
        selectedLocations,
        countCompanies,
        countContacts,
        bestLocation,
    } = props.selectedPoint;

    if (!bestLocation) {
        return null;
    }

    const limitedLocations = [bestLocation];
    const _filters = propsFilters && propsFilters.length > 0
        ? parseCubeFilter(propsFilters)
        : [];

    if (productDataType === 'RECORD') {
        _filters.push({
            member: `${cubeNamespace}.company_identity`,
            operator: 'equals',
            values: map(limitedLocations, loc => loc.id)
        });
    } else {
        _filters.push({
            member: `${cubeNamespace}.company_address_city`,
            operator: 'equals',
            values: map(limitedLocations, loc => loc.company_address_city)
        });
        _filters.push({
            member: `${cubeNamespace}.company_address_region`,
            operator: 'equals',
            values: map(limitedLocations, loc => loc.company_address_region)
        });
    }

    const countTradeTypesPayload = {
        measures: [`${cubeNamespace}.count_companies`],
        dimensions: [`${cubeNamespace}.company_class_primary_trade`],
        timeDimensions: [],
        filters: _filters,
    };

    try {
        const countResult = await CubeApi.load(
            config.services.ATLAS.cubeBaseUrl,
            countTradeTypesPayload
        );

        const tradeTypes = map(countResult, row => ({
            tradeType: row[`${cubeNamespace}.company_class_primary_trade`],
            count: parseInt(row[`${cubeNamespace}.count_companies`], 10)
        }));

        return {
            selectedLocations,
            countCompanies,
            countContacts,
            tradeTypes,
            bestLocation
        };
    } catch {
        return {
            selectedLocations,
            countCompanies,
            countContacts,
            bestLocation,
            tradeTypes: []
        };
    }
}
